/*
 * Requests (WORK > Requests): everyone who asked for work, from the website form or typed in by the office, and what
 * became of each. A request is a lead; its state comes from its pipeline stage and its quote, as the old Steward's
 * Requests list did from the quote (09-lists.js reqKey): New, Quote sent, Converted (approved work) or Lost.
 */
import { oneLine } from "./address.js";
import type { QuoteStatus } from "./billing.js";

export const REQUEST_STATES = ["new", "sent", "converted", "lost"] as const;
export type RequestState = (typeof REQUEST_STATES)[number];

export const REQUEST_STATE_WORDS: Record<RequestState, string> = {
  new: "New",
  sent: "Quote sent",
  converted: "Converted",
  lost: "Lost",
};

/** The state of one request: its quote leads, then a won or lost stage, else New. */
export function requestState(r: { stageFixed: string | null; quoteStatus: QuoteStatus | null }): RequestState {
  const q = r.quoteStatus;
  if (q === "approved" || q === "converted") return "converted";
  if (q === "declined" || q === "expired") return "lost";
  if (r.stageFixed === "won") return "converted";
  if (r.stageFixed === "lost") return "lost";
  if (q === "sent") return "sent";
  return "new";
}

/** Settled requests (became approved work, or lost) archive on their own. */
export function requestSettled(state: RequestState): boolean {
  return state === "converted" || state === "lost";
}

/**
 * Off the Requests list: archived by hand, or on its own once settled (converted or lost). Only the one archived by hand
 * is off the pipeline and the reports; settled ones stay there.
 */
export function requestArchived(r: { removed: boolean; state: RequestState }): boolean {
  return r.removed || requestSettled(r.state);
}

/**
 * An address with its ZIP once: adds the ZIP only when the address lacks it, and folds a ZIP said twice
 * ("70126 70126", from old website requests) into one.
 */
export function addressWithZip(address: string | null | undefined, zip?: string | null): string | null {
  const a = (address ?? "").trim().replace(/\b(\d{5})(?:[\s,]+\1)+\b/g, "$1");
  const z = (zip ?? "").trim();
  if (!z || a.includes(z)) return a || null;
  return a ? `${a}, ${z}` : z;
}

/** A lead's address on one line: from its parts when it has them, else the one line an older form saved. */
export function leadAddressLine(l: {
  address: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}): string | null {
  if (l.city || l.state)
    return oneLine({ street1: l.address, street2: l.address2, city: l.city, state: l.state, zip: l.zip }) || null;
  return addressWithZip(l.address, l.zip);
}

const DAY = 86_400_000;

export interface RequestStats {
  /** Requests not archived, by state. Converted and lost ones count archived or not (they archive on their own). */
  counts: Record<RequestState, number>;
  archived: number;
  /** Requests made in the past 30 days (archived ones too). */
  recent: number;
  /** Of those, the percent that became approved work. Null with none. */
  recentRate: number | null;
  all: number;
  converted: number;
  allRate: number | null;
}

/** The tiles on top of the Requests list (old Steward: New requests, Conversion rate, All time). */
export function requestStats(
  rows: readonly { state: RequestState; archived: boolean; createdAt: string }[],
  now = Date.now(),
): RequestStats {
  const counts: Record<RequestState, number> = { new: 0, sent: 0, converted: 0, lost: 0 };
  for (const r of rows) if (!r.archived || requestSettled(r.state)) counts[r.state] += 1;
  const since = now - 30 * DAY;
  const recent = rows.filter((r) => Date.parse(r.createdAt) >= since);
  const won = (xs: readonly { state: RequestState }[]) => xs.filter((r) => r.state === "converted").length;
  const rate = (n: number, of: number) => (of ? Math.round((n / of) * 100) : null);
  const converted = won(rows);
  return {
    counts,
    archived: rows.filter((r) => r.archived).length,
    recent: recent.length,
    recentRate: rate(won(recent), recent.length),
    all: rows.length,
    converted,
    allRate: rate(converted, rows.length),
  };
}
